import { useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppStore } from '@/store/appStore'
import { useIsExpanded } from '@/hooks/useLayoutMode'
import {
  SIDEBAR_DEFAULT_WIDTH,
  SIDEBAR_MAX_WIDTH,
  SIDEBAR_MIN_WIDTH,
  clampSidebarWidth,
} from '@/utils/sidebarWidth'
import { SidebarContent } from './SidebarContent'
import { MobileNavDrawer } from './MobileNavDrawer'

const KEYBOARD_STEP = 10

/**
 * Picks the shell for the sidebar body:
 *  - expanded (>= lg): the persistent dark <aside>, collapsible and resizable by dragging its edge
 *  - compact / medium: a temporary Drawer opened from the header hamburger
 *
 * `sidebarCollapsed` is intentionally read but never written below lg. A desktop
 * preference must survive a visit on a phone.
 */
export function Sidebar() {
  const { t } = useTranslation()
  const sidebarCollapsed = useAppStore((s) => s.sidebarCollapsed)
  const sidebarWidth = useAppStore((s) => s.sidebarWidth)
  const setSidebarWidth = useAppStore((s) => s.setSidebarWidth)
  const isExpanded = useIsExpanded()
  const [resizing, setResizing] = useState(false)
  const drag = useRef<{ startX: number; startWidth: number } | null>(null)

  if (!isExpanded) return <MobileNavDrawer />

  // A persisted width from an older range is pulled back inside the current one.
  const width = sidebarWidth === null ? SIDEBAR_DEFAULT_WIDTH : clampSidebarWidth(sidebarWidth)

  const handlePointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    e.preventDefault()
    e.currentTarget.setPointerCapture(e.pointerId)
    drag.current = { startX: e.clientX, startWidth: width }
    setResizing(true)
  }

  const handlePointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return
    setSidebarWidth(clampSidebarWidth(drag.current.startWidth + e.clientX - drag.current.startX))
  }

  const endDrag = () => {
    drag.current = null
    setResizing(false)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const next =
      e.key === 'ArrowLeft' ? width - KEYBOARD_STEP
      : e.key === 'ArrowRight' ? width + KEYBOARD_STEP
      : e.key === 'Home' ? SIDEBAR_MIN_WIDTH
      : e.key === 'End' ? SIDEBAR_MAX_WIDTH
      : null
    if (next === null) return
    e.preventDefault()
    setSidebarWidth(clampSidebarWidth(next))
  }

  // Setting the variable rather than `width` keeps `.sidebar--collapsed` in charge when collapsed.
  const style = sidebarWidth === null ? undefined : ({ '--sidebar-width': `${width}px` } as CSSProperties)

  return (
    <aside
      className={`sidebar${sidebarCollapsed ? ' sidebar--collapsed' : ''}${resizing ? ' sidebar--resizing' : ''}`}
      style={style}
      aria-label={t('sidebar.navigationSidebar')}
    >
      <SidebarContent variant="desktop" />
      <div
        className="sidebar-resize-handle"
        role="separator"
        aria-orientation="vertical"
        aria-label={t('sidebar.resizeSidebar')}
        aria-valuenow={width}
        aria-valuemin={SIDEBAR_MIN_WIDTH}
        aria-valuemax={SIDEBAR_MAX_WIDTH}
        tabIndex={0}
        title={t('sidebar.resizeSidebarHint')}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onDoubleClick={() => setSidebarWidth(null)}
        onKeyDown={handleKeyDown}
      />
    </aside>
  )
}
